import * as React from "react"

import { Skeleton, cn } from "@ibada/ui"

import { useTranslations } from "@/i18n"

/**
 * Building blocks for a page's first load. A page keeps its real title row and toolbar and swaps only its data
 * area for one of these, drawn in the shape of what will land (same grid, same card sizes, same row heights), so
 * nothing jumps when the data arrives. Shown only while there is no data yet: a background refetch keeps the
 * data on screen.
 */

/** The loading data area: announced once as busy, the placeholders inside stay out of the accessibility tree. */
export function LoadingRegion({
  label,
  className,
  children,
}: {
  /** What is loading, read by screen readers. Defaults to "Loading…". */
  label?: string
  /** The layout of the placeholders (the real data area's grid or stack classes). */
  className?: string
  children: React.ReactNode
}) {
  const t = useTranslations("common")
  return (
    <div role='status' aria-busy='true' aria-live='polite'>
      <span className='sr-only'>{label ?? t("loading")}</span>
      <div aria-hidden className={className}>
        {children}
      </div>
    </div>
  )
}

/** A placeholder card: the app's soft white container (16px radius, hairline border). */
export function SkeletonCard({ className, children }: { className?: string; children?: React.ReactNode }) {
  return <div className={cn("bg-card rounded-2xl border", className)}>{children}</div>
}

/** A short rounded bar standing in for a line of text. */
export function SkeletonLine({ className }: { className?: string }) {
  return <Skeleton className={cn("h-3 rounded-full", className)} />
}

/** A pill (a chip, a status, a small button) or a round avatar when given a square size. */
export function SkeletonPill({ className }: { className?: string }) {
  return <Skeleton className={cn("h-6 w-16 rounded-full", className)} />
}

/** A grid of identical placeholder tiles, e.g. the departments or the funds grid. */
export function SkeletonTileGrid({
  count,
  className,
  renderTile,
}: {
  count: number
  /** The real grid's classes (columns and gap). */
  className: string
  renderTile: (index: number) => React.ReactNode
}) {
  return (
    <div className={className}>
      {Array.from({ length: count }, (_, index) => (
        <React.Fragment key={index}>{renderTile(index)}</React.Fragment>
      ))}
    </div>
  )
}

/** The bar widths rows cycle through, so a list of placeholders reads like real, uneven text. */
const ROW_WIDTHS = ["w-2/5", "w-1/3", "w-1/2", "w-2/5", "w-1/4", "w-1/3"] as const

/**
 * A card of rows (a grouped list): an optional header bar, then `rows` divided rows with a leading mark, two lines
 * of text and a trailing pill.
 */
export function SkeletonRowsCard({
  rows,
  header = true,
  headerHint = false,
  leading = true,
  trailing = true,
  className,
  rowClassName,
}: {
  rows: number
  header?: boolean
  /** A muted hint at the header's right end. */
  headerHint?: boolean
  /** A round mark before the text (an avatar, an icon tile). */
  leading?: boolean
  trailing?: boolean
  className?: string
  /** The real row's padding and height, so the card has the same height as the loaded one. */
  rowClassName?: string
}) {
  return (
    <SkeletonCard className={cn("overflow-hidden", className)}>
      {header ? (
        <div className='flex items-center gap-2 border-b px-[18px] py-3.5'>
          <SkeletonLine className='h-3.5 w-32' />
          <SkeletonLine className='w-6' />
          {headerHint ? <SkeletonLine className='ml-auto w-40' /> : null}
        </div>
      ) : null}
      <div className='divide-y'>
        {Array.from({ length: rows }, (_, index) => (
          <div key={index} className={cn("flex items-center gap-3 px-[18px] py-3", rowClassName)}>
            {leading ? <Skeleton className='size-9 shrink-0 rounded-full' /> : null}
            <div className='flex min-w-0 flex-1 flex-col gap-2'>
              <SkeletonLine className={cn("h-3.5", ROW_WIDTHS[index % ROW_WIDTHS.length])} />
              <SkeletonLine className='w-1/5' />
            </div>
            {trailing ? <SkeletonPill className='shrink-0' /> : null}
          </div>
        ))}
      </div>
    </SkeletonCard>
  )
}

/** A figure card: a small label, a big number, a short caption. */
export function SkeletonStatCard({ className }: { className?: string }) {
  return (
    <SkeletonCard className={cn("flex flex-col gap-3 px-4 py-3.5", className)}>
      <SkeletonLine className='w-24' />
      <Skeleton className='h-7 w-28 rounded-lg' />
      <SkeletonLine className='w-32' />
    </SkeletonCard>
  )
}

/** A settings or form card: a title, then label and field pairs (fields are pills, like the real inputs). */
export function SkeletonFormCard({ fields = 3, className }: { fields?: number; className?: string }) {
  return (
    <SkeletonCard className={cn("flex flex-col gap-5 p-6", className)}>
      <div className='flex flex-col gap-2'>
        <SkeletonLine className='h-4 w-40' />
        <SkeletonLine className='w-64' />
      </div>
      {Array.from({ length: fields }, (_, index) => (
        <div key={index} className='flex flex-col gap-2'>
          <SkeletonLine className='w-28' />
          <Skeleton className='h-9 w-full rounded-full' />
        </div>
      ))}
    </SkeletonCard>
  )
}
